import {
    Component,
    ComponentInterface,
    Prop,
    Element,
    Watch,
    State,
    Host,
    h,
} from "@stencil/core";
import api from "../../utils/api";
import serializeForm from "../../utils/serializeForm";
import waitForHydrated from "../../utils/waitForHydrated";
import {
    NotificationItem,
    NotificationCenterResponse,
} from "./notification-center-response";
import row from "./row";

@Component({
    tag: "ux-iws-data-controller-notification-center",
})
export class DataControllerNotificationCenter implements ComponentInterface {
    @Element() element: HTMLUxIwsDataControllerNotificationCenterElement;
    /** endpoint where data can be requested */
    @Prop() url: string = "";
    /** CSS-style selector for all notifications */
    @Prop() notificationSelector: string = "";
    /** CSS-style selector for the filter */
    @Prop() noNotificationSelector: string = "";
    /** CSS-style selector for the filter */
    @Prop() filterSelector: string = "";
    /** CSS-style selector for the filter results */
    @Prop() resultSelector: string = "";
    /** CSS-style selector for the scroll-sensor */
    @Prop() scrollSensorSelector: string = "";
    /** CSS-style selector for the notification counter */
    @Prop() notificationCounterSelector: string = "";
    /** Indicates the active tab */
    @State() activeTab: boolean = false;
    /** Indicates if notifications are available for the active tab */
    @State() notificationsAvailable: boolean = false;

    /** notification element that contains all notifications */
    private notificationElement: HTMLDivElement | null = null;
    /** notification element if no notifications available */
    private noNotificationElement: HTMLDivElement | null = null;
    /** element that contains all filters */
    private filterElement: HTMLUxFilterElement | null = null;
    /** form element for filter */
    private filterFormElement: HTMLFormElement | null = null;
    /** filter result div container */
    private resultContainerElement: HTMLDivElement | null = null;
    /** single item result row element */
    private notificationItemRowElement: HTMLDivElement | null = null;
    /** notification counter element */
    private notificationCounterElement: HTMLDivElement | null = null;
    /** modal spinner element */
    private modalSpinnerElement = document.querySelector("ux-spinner-overlay");
    /** notification counter */
    private notificationCounter: string = "";
    /** URL for getting the next page */
    private nextPage: string = "";
    /** DOM element where all the tiles should be put */
    private scrollSensor: HTMLUxInfiniteScrollSensorElement | null = null;

    componentDidLoad() {
        this.setActiveTab();
        this.setActiveTabListener();
        this.setNotificationContainer();
        this.setFilter();
        this.setResults();
        this.setResultsCounter();
        this.setScrollSensor();
        this.requestOnNotificationUpdate();
    }

    /** notification center filter  */
    @Watch("notificationSelector")
    setNotificationContainer() {
        if (this.notificationSelector === "") return;
        this.notificationElement = this.element.querySelector(
            this.notificationSelector
        ) as HTMLDivElement;
        this.noNotificationElement = this.element.querySelector(
            ".ux-iws-notification-center__no-notifications"
        ) as HTMLDivElement;
    }

    /** notification center filter  */
    @Watch("filterSelector")
    setFilter() {
        if (this.filterSelector === "") return;
        this.filterElement = this.element.querySelector(
            this.filterSelector
        ) as HTMLUxFilterElement;
        waitForHydrated(this.filterElement, () => this.setFilterForm());
        this.filterElement.addEventListener("uxFilterChange", () => {
            this.requestData(this.url);
        });
    }

    /** notification center filter results */
    @Watch("notificationCounterSelector")
    setResultsCounter() {
        if (this.notificationCounterSelector === "") return;
        this.notificationCounterElement = this.element.querySelector(
            this.notificationCounterSelector
        ) as HTMLDivElement;
    }

    /** notification center filter results */
    @Watch("resultSelector")
    setResults() {
        if (this.resultSelector === "") return;
        this.resultContainerElement = this.element.querySelector(
            this.resultSelector
        ) as HTMLDivElement;
        this.notificationItemRowElement = this.resultContainerElement;
    }

    /** connect scroll sensor */
    @Watch("scrollSensorSelector")
    setScrollSensor() {
        if (this.scrollSensorSelector === "") return;
        this.scrollSensor = this.element.querySelector(
            this.scrollSensorSelector
        ) as HTMLUxInfiniteScrollSensorElement;
        if (this.scrollSensor !== null) {
            this.scrollSensor.addEventListener(
                "uxInfiniteScrollSensorScrolledIntoView",
                () => {
                    if (this.activeTab) this.requestData(this.nextPage);
                }
            );
        }
    }

    /** add active tab listener */
    private setActiveTabListener = () => {
        document.addEventListener("uxActiveTabSet", (event: CustomEvent) =>
            this.setActiveTab(event.detail)
        );
    };

    /** update notification center on item close success */
    private requestOnNotificationUpdate = () => {
        this.element.addEventListener(
            "uxUpdateNotificationItem",
            (event: CustomEvent) => {
                if (event.detail.success) this.requestData(this.url);
            }
        );
    };

    /** set active tab */
    private setActiveTab = (activeTabId?: string) => {
        activeTabId =
            activeTabId ||
            this.element
                .closest("ux-tab-container")
                .getAttribute("active-tab-id");
        const tabId = this.element.closest("ux-tab").getAttribute("tab-id");
        const isCurrentTabActive = activeTabId === tabId;
        this.activeTab = isCurrentTabActive;
        if (this.scrollSensor !== null) {
            this.scrollSensor.hasFired = !isCurrentTabActive;
        }

        if (isCurrentTabActive) {
            this.requestData(this.url);
        }
    };

    /** set filter form */
    private setFilterForm() {
        this.filterFormElement = this.filterElement.querySelector(
            "form"
        ) as HTMLFormElement;
        this.filterFormElement?.addEventListener("submit", (e) =>
            e.preventDefault()
        );
    }

    /** Send request and handle response */
    private async requestData(url: string) {
        if (url === "") return;
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body: serializeForm(this.filterFormElement),
                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };

        this.showLoadingSpinner();

        try {
            const data = await api<NotificationCenterResponse>(url, options);

            if (isMockUrl(url))
                await new Promise((resolve) => setTimeout(resolve, 1000));

            if (this.url === url) this.clearFilterResults();

            if (data) {
                if (data.items.length > 0) {
                    this.appendNotificationItems(data.items);
                    this.notificationsAvailable = true;
                    this.nextPage = data.nextPage || "";
                    this.notificationCounter =
                        data.notificationCounter.toString();
                    this.notificationCounterElement.querySelector(
                        ".notification-center-result-counter-number"
                    ).textContent = this.notificationCounter;
                    this.notificationCounterElement.classList.add("show");
                    this.notificationElement.classList.add("show");
                }

                if (!data.items || data.items.length == 0) {
                    this.notificationsAvailable = false;
                    this.noNotificationElement.classList.add("show");
                }

                if (this.nextPage !== "" && this.scrollSensor !== null) {
                    this.scrollSensor.hasFired = false;
                }
            }
        } catch (e) {
            console.log(e);
        }

        this.hideLoadingSpinner();
    }

    /** show loading spinner */
    private showLoadingSpinner() {
        if (this.modalSpinnerElement === null) {
            this.modalSpinnerElement =
                document.createElement("ux-spinner-overlay");
            this.element.parentElement.append(this.modalSpinnerElement);
        }
        this.modalSpinnerElement.opened = true;
    }

    /** hide loading spinner */
    private hideLoadingSpinner() {
        this.modalSpinnerElement.opened = false;
    }

    /** clear filter results every time the filter is updated */
    private clearFilterResults() {
        if (this.notificationItemRowElement !== null)
            this.notificationItemRowElement.textContent = "";
    }

    /** append notification items to notification results container */
    private appendNotificationItems(items: NotificationItem[]) {
        if (this.notificationItemRowElement === null) return;
        const rowItems = items.map((item) => ({
            ...item,
        }));
        this.notificationItemRowElement.insertAdjacentHTML(
            "beforeend",
            rowItems.map(row).join("")
        );
    }

    render() {
        return (
            <Host>
                {this.notificationsAvailable ? (
                    <slot name="notifications"></slot>
                ) : (
                    <div class="ux-iws-notification-center__no-notifications">
                        <slot name="no-notifications"></slot>
                    </div>
                )}
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}
